import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Header from './components/Header';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import NewUserDashboard from './pages/NewUserDashboard';
import NewAdminDashboard from './pages/NewAdminDashboard';
import Profile from './pages/Profile';
import UserManagement from './pages/UserManagement';
import UpgradePage from './pages/UpgradePage';
import UpgradeManagement from './pages/UpgradeManagement';
import ReferralsPage from './pages/ReferralsPage';
import AdminReferralsManagement from './pages/AdminReferralsManagement';
import AdminWithdrawalManagement from './pages/AdminWithdrawalManagement';
import UserTasks from './pages/UserTasks';
import TaskManagement from './pages/TaskManagement';
import AdminTaskReviewDashboard from './pages/AdminTaskReviewDashboard';
import AdminProfileManagement from './pages/AdminProfileManagement';
import ForgotPassword from './pages/ForgotPassword';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import WithdrawalRequests from './pages/WithdrawalRequests';
import RewardManagement from './pages/RewardManagement';
import TransactionManagement from './pages/TransactionManagement';
import TransactionHistory from './pages/TransactionHistory';
import AdminAnalytics from './pages/AdminAnalytics';
import HelpSupport from './pages/HelpSupport';
import DashboardRouter from './components/DashboardRouter';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';

// Smart redirect component based on user role
const RoleBasedRedirect = () => {
  const { user } = useAuth();
  
  if (user?.role === 'admin') {
    return <Navigate to="/app/admin/users" replace />;
  }
  
  return <Navigate to="/app/dashboard" replace />;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public Routes with Header */}
            <Route path="/" element={
              <>
                <Header />
                <LandingPage />
              </>
            } />
            <Route path="/login" element={
              <>
                <Header />
                <Login />
              </>
            } />
            <Route path="/register" element={
              <>
                <Header />
                <Register />
              </>
            } />
            <Route path="/forgot-password" element={
              <>
                <Header />
                <ForgotPassword />
              </>
            } />
            <Route path="/verify-email/:token" element={
              <>
                <Header />
                <VerifyEmail />
              </>
            } />
            <Route path="/terms" element={
              <>
                <Header />
                <TermsAndConditions />
              </>
            } />
            <Route path="/privacy" element={
              <>
                <Header />
                <PrivacyPolicy />
              </>
            } />
            
            {/* Protected Routes - All use unified AdminLTE Layout */}
            <Route path="/app" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<RoleBasedRedirect />} />
              
              {/* User Dashboard Routes */}
              <Route path="dashboard" element={
                <ProtectedRoute userOnly>
                  <DashboardRouter />
                </ProtectedRoute>
              } />
              <Route path="tasks" element={
                <ProtectedRoute userOnly>
                  <UserTasks />
                </ProtectedRoute>
              } />
              <Route path="transactions" element={
                <ProtectedRoute userOnly>
                  <TransactionHistory />
                </ProtectedRoute>
              } />
              <Route path="profile" element={
                <ProtectedRoute userOnly>
                  <Profile />
                </ProtectedRoute>
              } />
              <Route path="upgrade" element={
                <ProtectedRoute userOnly>
                  <UpgradePage />
                </ProtectedRoute>
              } />
              <Route path="referrals" element={
                <ProtectedRoute userOnly>
                  <ReferralsPage />
                </ProtectedRoute>
              } />
              <Route path="withdrawals" element={
                <ProtectedRoute userOnly>
                  <WithdrawalRequests />
                </ProtectedRoute>
              } />
              <Route path="settings" element={
                <ProtectedRoute userOnly>
                  <div className="admin-card"><div className="admin-card-body">Settings Page - Coming Soon</div></div>
                </ProtectedRoute>
              } />
              <Route path="help" element={
                <ProtectedRoute>
                  <HelpSupport />
                </ProtectedRoute>
              } />
              
              {/* Admin Routes */}
              <Route path="admin/users" element={
                <ProtectedRoute adminOnly>
                  <UserManagement />
                </ProtectedRoute>
              } />
              <Route path="admin/tasks" element={
                <ProtectedRoute adminOnly>
                  <TaskManagement />
                </ProtectedRoute>
              } />
              <Route path="admin/task-reviews" element={
                <ProtectedRoute adminOnly>
                  <AdminTaskReviewDashboard />
                </ProtectedRoute>
              } />
              <Route path="admin/rewards" element={
                <ProtectedRoute adminOnly>
                  <RewardManagement />
                </ProtectedRoute>
              } />
              <Route path="admin/analytics" element={
                <ProtectedRoute adminOnly>
                  <AdminAnalytics />
                </ProtectedRoute>
              } />
              <Route path="admin/upgrades" element={
                <ProtectedRoute adminOnly>
                  <UpgradeManagement />
                </ProtectedRoute>
              } />
              <Route path="admin/referrals" element={
                <ProtectedRoute adminOnly>
                  <AdminReferralsManagement />
                </ProtectedRoute>
              } />              <Route path="admin/withdrawals" element={
                <ProtectedRoute adminOnly>
                  <AdminWithdrawalManagement />
                </ProtectedRoute>
              } />              <Route path="admin/transactions" element={
                <ProtectedRoute adminOnly>
                  <TransactionManagement />
                </ProtectedRoute>
              } />              <Route path="admin/profiles" element={
                <ProtectedRoute adminOnly>
                  <AdminProfileManagement />
                </ProtectedRoute>
              } />
              <Route path="admin/settings" element={
                <ProtectedRoute adminOnly>
                  <div className="admin-card">
                    <div className="admin-card-header">
                      <h5 className="mb-0"><i className="fas fa-cog me-2"></i>Admin Settings</h5>
                    </div>
                    <div className="admin-card-body">Admin Settings Page - Coming Soon</div>
                  </div>
                </ProtectedRoute>
              } />
            </Route>
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
